/**
 * Модальное окно выбора preferred_channel и источника изменения для пациента.
 * Дата: 07.04.2026
 * Связан с: Patients.tsx, patientsApi, ChannelBadges
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Select } from '@/components/ui';
import {
  PatientPreferenceSourceDTO,
  PatientPreferredChannelHistoryItemDTO,
  PatientWithChannelsDTO,
  PatientAvailableChannels,
} from '@/api/patients';
import { ChannelIcon } from '@/components/ui/ChannelIcon';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/formatters';

const CHANNEL_ORDER: (keyof PatientAvailableChannels)[] = [
  'telegram_bot',
  'telegram_client',
  'whatsapp_waba',
  'whatsapp_regular',
  'max',
  'sms',
  'sms_tel',
  'phone',
];

const CHANNEL_LABELS: Record<string, string> = {
  auto: 'Auto (глобальные настройки)',
  telegram_bot: 'Telegram Bot',
  telegram_client: 'Telegram Client',
  whatsapp_waba: 'WhatsApp WABA',
  whatsapp_regular: 'WhatsApp',
  max: 'MAX (VK)',
  sms: 'SMS',
  sms_tel: 'SMS-Tel',
  phone: 'Телефон',
};

const CONTACT_MODE_OPTIONS = [
  { value: 'any', label: '∞ Любой контакт' },
  { value: 'written_only', label: '✎ Только письменно' },
  { value: 'voice_only', label: '☎ Только звонок' },
  { value: 'do_not_contact', label: '⊘ Не беспокоить' },
] as const;

interface PatientChannelSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  patient: PatientWithChannelsDTO | null;
  sourceOptions: PatientPreferenceSourceDTO[];
  historyItems: PatientPreferredChannelHistoryItemDTO[];
  historyLoading: boolean;
  onSave: (channel: string | null, contactMode: string, sourceCode: string) => Promise<void>;
}

function getChannelLabel(channel: string | null | undefined) {
  if (!channel) return CHANNEL_LABELS.auto;
  return CHANNEL_LABELS[channel] || channel;
}

export function PatientChannelSelector({
  isOpen,
  onClose,
  patient,
  sourceOptions,
  historyItems,
  historyLoading,
  onSave,
}: PatientChannelSelectorProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedContactMode, setSelectedContactMode] = useState('any');
  const [selectedSource, setSelectedSource] = useState('');
  const [saving, setSaving] = useState(false);

  const currentChannel = patient?.preferred_channel ?? null;
  const currentContactMode = patient?.contact_mode || 'any';
  const currentSourceCode = patient?.preferred_channel_source_code ?? '';
  const channels = patient?.available_channels ?? {};

  const sourceSelectOptions = useMemo(
    () => [
      { value: '', label: 'Выберите источник изменения' },
      ...sourceOptions.map((item) => ({ value: item.code, label: item.display_name })),
    ],
    [sourceOptions]
  );

  useEffect(() => {
    if (patient && isOpen) {
      setSelected(currentChannel ?? 'auto');
      setSelectedContactMode(currentContactMode);
      const defaultSource = currentSourceCode || sourceOptions.find((item) => item.code === 'admin_selected')?.code || sourceOptions[0]?.code || '';
      setSelectedSource(defaultSource);
    }
  }, [patient, isOpen, currentChannel, currentContactMode, currentSourceCode, sourceOptions]);

  const handleSave = async () => {
    if (!patient || !selectedSource) return;
    setSaving(true);
    try {
      const value = selected === 'auto' ? null : selected;
      await onSave(value, selectedContactMode, selectedSource);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!patient) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Настройки уведомлений: ${patient.full_name || patient.phone_normalized || patient.phone}`}
      size="lg"
      testId="patient-channel-selector"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={saving || !selectedSource} testId="patient-channel-save">
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <p className="text-sm text-neutral-600">
            Выберите канал для уведомлений пациента. «Auto» оставляет выбор на глобальных настройках tenant.
          </p>
          {(patient.preferred_channel_source_label || patient.preferred_channel_set_at) && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
              Последнее изменение: {patient.preferred_channel_source_label || patient.preferred_channel_source_code || '—'}
              {patient.preferred_channel_set_at ? ` · ${formatDateTime(patient.preferred_channel_set_at)}` : ''}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-neutral-900">Режим контакта</div>
          {(patient.contact_mode_source_label || patient.contact_mode_set_at) && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
              Режим: {patient.contact_mode_source_label || patient.contact_mode_source_code || '—'}
              {patient.contact_mode_set_at ? ` · ${formatDateTime(patient.contact_mode_set_at)}` : ''}
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            {CONTACT_MODE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
                  selectedContactMode === option.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                )}
              >
                <input
                  type="radio"
                  name="contact_mode"
                  value={option.value}
                  checked={selectedContactMode === option.value}
                  onChange={() => setSelectedContactMode(option.value)}
                  className="h-4 w-4"
                />
                <span className="font-medium">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <label
            className={cn(
              'flex items-center gap-3 rounded-lg border p-3 transition-colors',
              (selected ?? 'auto') === 'auto'
                ? 'cursor-pointer border-primary-500 bg-primary-50'
                : 'cursor-pointer border-neutral-200 hover:border-neutral-300'
            )}
          >
            <input
              type="radio"
              name="channel"
              value="auto"
              checked={selected === 'auto'}
              onChange={() => setSelected('auto')}
              className="h-4 w-4"
            />
            <span className="font-medium">{CHANNEL_LABELS.auto}</span>
          </label>

          {CHANNEL_ORDER.map((key) => {
            const isAvailable = channels[key] ?? false;
            const label = CHANNEL_LABELS[key] || key;
            const value = key;

            return (
              <label
                key={key}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                  selected === value ? 'border-primary-500 bg-primary-50' : 'border-neutral-200',
                  isAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                )}
              >
                <input
                  type="radio"
                  name="channel"
                  value={value}
                  checked={selected === value}
                  onChange={() => isAvailable && setSelected(value)}
                  disabled={!isAvailable}
                  className="h-4 w-4"
                />
                <ChannelIcon channelId={value} size={18} />
                <span className="font-medium">{label}</span>
                <span className={cn('ml-auto text-xs', isAvailable ? 'text-green-600' : 'text-neutral-400')}>
                  {isAvailable ? 'Доступен' : 'Не подключён'}
                </span>
              </label>
            );
          })}
        </div>

        <Select
          value={selectedSource}
          onChange={(event) => setSelectedSource(event.target.value)}
          label="Источник изменения"
          options={sourceSelectOptions}
          error={!selectedSource ? 'Источник обязателен для сохранения истории.' : undefined}
          testId="patient-channel-source"
        />

        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-neutral-900">Последние изменения</div>
            <div className="text-xs text-neutral-500">
              История помогает понять, кто и откуда назначил текущий `preferred_channel`.
            </div>
          </div>

          {historyLoading ? (
            <div className="rounded-lg border border-neutral-200 px-3 py-4 text-sm text-neutral-500">
              Загружаем историю изменений...
            </div>
          ) : historyItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-neutral-200 px-3 py-4 text-sm text-neutral-500">
              История пока пустая.
            </div>
          ) : (
            <div className="space-y-2">
              {historyItems.map((item) => (
                <div key={item.id} className="rounded-lg border border-neutral-200 px-3 py-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium text-neutral-900">
                      {getChannelLabel(item.old_channel)} {'→'} {getChannelLabel(item.new_channel)}
                    </div>
                    <div className="text-xs text-neutral-500">{formatDateTime(item.created_at)}</div>
                  </div>
                  <div className="mt-1 text-xs text-neutral-600">
                    {item.source_label || item.source_code}
                    {(item.actor_name || item.actor_type) ? ` · ${item.actor_name || item.actor_type}` : ''}
                  </div>
                  {item.note && <div className="mt-1 text-xs text-neutral-500">{item.note}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
